import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DateRangeField } from "../../atoms";
import {
  FaTimes,
  FaHistory,
  FaChartLine,
  FaClipboardList,
  FaListUl,
  FaInfoCircle,
  FaArrowLeft,
  FaArrowRight,
  FaClock,
  FaCalendarAlt,
  FaSearch,
  FaCheckCircle,
  FaExclamationTriangle,
  FaWarehouse,
  FaBuilding,
  FaCogs,
  FaUser,
} from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import CustomStyles from "../../../CustomStyles";
import { chartColors } from "../../../Config/Colors";
import useAuth from "../../../Hooks/useAuth";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";
import { MascotLoadingOverlay } from "../../shared";
import usePermission from "../../../Hooks/usePermission";
import { getStepLabel, getStepKeyLabel } from "../../../utils/operationMetadata";
import { getMetadataLabel } from "../../../utils/operationMetadata";
import { displayLogEntry, displayLogInfoName, displayLogText } from "../../../utils/deviceActivityLabels";
import {
  useGetDeviceLastOperationMutation,
  useGetDeviceConsumentStatsMutation,
  useGetDeviceSettingLogsPaginatedMutation,
  useGetDeviceOperationLogsPaginatedMutation,
  useGetDeviceInfoLogsPaginatedMutation,
} from "../../../store/storageRoles/devicesSharedApi";
import {
  useGetDealerRolesDeviceConsumentStatsMutation,
  useGetDealerRolesDeviceSettingLogsPaginatedMutation,
  useGetDealerRolesDeviceOperationLogsPaginatedMutation,
} from "../../../store/dealerRoles/devicesApi";

import mascotNoData from "../../../Assets/icons/okkali_mascot_nodata.png";

/**
 * Firma > Cihazlar tablosu — "Operasyonlar" menüsünden seçilen detay
 * görünümünü gösteren sağ drawer (ManualQuotaDrawer kabuk deseni).
 * İçerikte cihaz kartı YOKTUR; cihaz kimliği header'da, seçilen görünüm
 * drawer gövdesinde geniş fontlarla sıfırdan tasarlanmıştır.
 */

// Her görünümün kendi rengi var; `gradient` header'da kullanılır (OperationDrawer
// ve ManualQuotaDrawer ile aynı desen: renkli başlık + beyaz metin), `color`/`bg`
// ise gövde içindeki ikon çipleri için kalır.
const VIEW_META = {
  lastOperation: { title: "Son İşlem", icon: FaHistory, color: "text-violet-600", bg: "bg-violet-100", gradient: "from-violet-600 to-purple-700" },
  openPerformance: { title: "Performans", icon: FaChartLine, color: "text-indigo-600", bg: "bg-indigo-100", gradient: "from-indigo-600 to-blue-700" },
  settingLogs: { title: "Ayar Değişiklikleri", icon: FaClipboardList, color: "text-orange-600", bg: "bg-orange-100", gradient: "from-orange-500 to-amber-600" },
  operationLogs: { title: "Operasyonlar", icon: FaListUl, color: "text-cyan-600", bg: "bg-cyan-100", gradient: "from-cyan-600 to-sky-700" },
  infoLogs: { title: "Bilgi Değişiklikleri", icon: FaInfoCircle, color: "text-pink-600", bg: "bg-pink-100", gradient: "from-pink-600 to-rose-700" },
};

const DEFAULT_HEADER_GRADIENT = "from-slate-700 to-slate-800";

const PAGE_LIMIT = 20;

// ── Yardımcılar ──

const getTimeSince = (dateStr) => {
  if (!dateStr) return null;
  const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diffMin < 60) return `${diffMin} dk önce`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} saat önce`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 30) return `${diffDay} gün önce`;
  return `${Math.floor(diffDay / 30)} ay önce`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return (
    d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
  );
};

const PRIMARY_PRODUCTS = new Set(["Çay", "Türk Kahvesi", "Filtre Kahve"]);
const colorOfProduct = {
  Çay: CustomStyles.productColors["Çay"],
  "Türk Kahvesi": CustomStyles.productColors["Türk Kahvesi"],
  "Filtre Kahve": CustomStyles.productColors["Filtre Kahve"],
  Espresso: CustomStyles.productColors["Espresso"],
};

function normalizePerformanceTotals(logs) {
  if (!logs || !Array.isArray(logs)) return { productTotals: [], totalConsumption: 0, hourlyData: [] };
  const productTotalsMap = new Map();
  const hourlyMap = new Map();

  for (const log of logs) {
    const normalized = {};
    (log.consumentInfo || []).forEach((product) => {
      const name = PRIMARY_PRODUCTS.has(product.productName) ? product.productName : "Espresso";
      normalized[name] = (normalized[name] || 0) + (product.consument || 0);
    });
    let hourTotal = 0;
    Object.entries(normalized).forEach(([productName, consument]) => {
      hourTotal += consument || 0;
      productTotalsMap.set(productName, (productTotalsMap.get(productName) || 0) + (consument || 0));
    });
    hourlyMap.set(log.createdInfo, (hourlyMap.get(log.createdInfo) || 0) + hourTotal);
  }

  const productTotals = Array.from(productTotalsMap.entries())
    .map(([productName, total]) => ({ productName, total }))
    .sort((a, b) => b.total - a.total);
  const totalConsumption = productTotals.reduce((s, p) => s + p.total, 0);

  const hourlyData = [];
  for (let h = 0; h < 24; h++) {
    const key = h.toString().padStart(2, "0");
    hourlyData.push({ hour: key, total: hourlyMap.get(key) || 0 });
  }
  return { productTotals, totalConsumption, hourlyData };
}

// Adım tipi / alan etiketleri tek kaynaktan gelir (utils/operationMetadata).

const formatStepValue = (key, value) => {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Evet" : "Hayır";
  if (typeof value === "number") {
    if (key === "temperature" || key?.toLowerCase().includes("temperature")) return `${value}°C`;
    return String(value);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getStepStyle = (type) => {
  const n = String(type || "").toLowerCase();
  if (n.includes("completed") || n.includes("success")) {
    return { icon: <FaCheckCircle className="text-green-500 w-4 h-4" />, bg: "bg-green-50 border-green-200", text: "text-green-700" };
  }
  if (n.includes("failed") || n.includes("warning")) {
    return { icon: <FaExclamationTriangle className="text-amber-500 w-4 h-4" />, bg: "bg-amber-50 border-amber-200", text: "text-amber-700" };
  }
  return { icon: <FaInfoCircle className="text-blue-500 w-4 h-4" />, bg: "bg-blue-50 border-blue-200", text: "text-blue-700" };
};

const locationColorMap = {
  Depo: "bg-purple-50 text-purple-700", Firma: "bg-indigo-50 text-indigo-700",
  Servis: "bg-teal-50 text-teal-700", Plasiyer: "bg-blue-50 text-blue-700",
  Üretim: "bg-orange-50 text-orange-700",
};
const locationIconMap = { Depo: FaWarehouse, Firma: FaBuilding, Servis: FaCogs, Plasiyer: FaUser };

const LocationBadge = ({ value }) => {
  const Icon = locationIconMap[value];
  const color = locationColorMap[value] || "bg-gray-50 text-gray-700";
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${color}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {value || "-"}
    </span>
  );
};

const HourlyChart = ({ hourlyData }) => {
  const maxVal = Math.max(0, ...(hourlyData || []).map((d) => d.total || 0));
  const niceMax = maxVal <= 4 ? 4 : Math.ceil(maxVal / 4) * 4;
  const yTicks = [0, niceMax / 2, niceMax];

  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={hourlyData} margin={{ top: 6, right: 6, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
        <XAxis
          dataKey="hour"
          stroke={chartColors.axis}
          fontSize={12}
          tickLine={false}
          axisLine={{ stroke: chartColors.grid }}
          interval={3}
          tickFormatter={(v) => `${String(v).padStart(2, "0")}`}
        />
        <YAxis
          stroke={chartColors.axis}
          fontSize={12}
          tickLine={false}
          axisLine={false}
          width={34}
          allowDecimals={false}
          domain={[0, niceMax]}
          ticks={yTicks}
        />
        <Tooltip
          formatter={(value) => [value, "Tüketim"]}
          labelFormatter={(label) => `${label}:00`}
          contentStyle={{
            backgroundColor: "rgba(255,255,255,0.96)",
            border: "none",
            borderRadius: "8px",
            fontSize: "13px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            padding: "8px 12px",
          }}
          cursor={{ fill: "rgba(99,102,241,0.08)" }}
        />
        <Bar dataKey="total" fill={chartColors.bar} radius={[4, 4, 0, 0]} maxBarSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const EmptyView = ({ message, suggestion }) => (
  <div className="flex flex-col items-center justify-center py-12 select-none">
    <img src={mascotNoData} alt={message} className="w-24 h-24 object-contain mb-3 drop-shadow-md" draggable={false} />
    <p className="text-base font-semibold text-gray-700">{message}</p>
    {suggestion && <p className="text-sm text-gray-500 mt-1">{suggestion}</p>}
  </div>
);

/** Drawer gövdesindeki kart iskeleti — gerçek içerikle aynı kutu ölçüsünde. */
const CardSkeleton = () => (
  <div className="animate-pulse rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
    <div className="flex items-center gap-2.5 px-4 py-3 bg-gray-50 border-b border-gray-100">
      <div className="w-7 h-7 rounded-lg bg-gray-200" />
      <div className="h-3.5 w-32 bg-gray-200 rounded" />
    </div>
    <div className="px-4 py-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="h-3 w-24 bg-gray-200 rounded" />
        <div className="h-3 w-20 bg-gray-200 rounded" />
      </div>
      <div className="flex items-center justify-between">
        <div className="h-3 w-20 bg-gray-200 rounded" />
        <div className="h-3 w-28 bg-gray-200 rounded" />
      </div>
      <div className="h-3 w-2/3 bg-gray-200 rounded" />
    </div>
  </div>
);

/**
 * Yükleme iskeleti — spinner yerine içerik şeklinde bekleme.
 * Kural: skeleton'ların ÜSTÜNDE her zaman MascotLoadingOverlay bulunur
 * (bkz. docs/rules/ui-components.md).
 */
const LoadingView = ({ message = "Yükleniyor...", count = 3 }) => (
  <div className="relative min-h-[240px]">
    <MascotLoadingOverlay message={message} />
    <div className="space-y-3 opacity-40 pointer-events-none">
      {[...Array(count)].map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  </div>
);

const LoadMoreButton = ({ onClick, loading }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="w-full py-2.5 text-sm font-semibold text-indigo-600 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors disabled:opacity-50"
  >
    {loading ? "Yükleniyor..." : "Daha Fazla Yükle"}
  </button>
);

const DeviceOperationsDrawer = ({
  open,
  onClose,
  device: deviceProp,
  initialView: initialViewProp = null,
}) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const isDealerRole = auth?.role === "dealer";
  const token = auth?.accessToken;

  // Giriş/çıkış kayması + kapanış boyunca cihaz ve görünümün dondurulması.
  // Çağıran taraf kapatırken deviceId ve view'ı null'a çekiyor; donmazsa
  // başlık/renk (VIEW_META) ve içerik drawer hâlâ ekrandayken değişiyor.
  const frozenInput = useMemo(
    () => ({ device: deviceProp, initialView: initialViewProp }),
    [deviceProp, initialViewProp]
  );
  const { mounted, entered, data: frozen } = useDrawerTransition(open, frozenInput);
  const device = frozen?.device ?? deviceProp;
  const initialView = frozen?.initialView ?? initialViewProp;

  const meta = VIEW_META[initialView] || null;

  // ── Rol bazlı mutation seçimi ──
  // Son İşlem yalnız depoda — bayi görmez (#180).
  const [getDeviceLastOperation] = useGetDeviceLastOperationMutation();

  const [getStorageStats] = useGetDeviceConsumentStatsMutation();
  const [getDealerStats] = useGetDealerRolesDeviceConsumentStatsMutation();
  const getStats = isDealerRole ? getDealerStats : getStorageStats;

  const [fetchStorageSettingLogs] = useGetDeviceSettingLogsPaginatedMutation();
  const [fetchDealerSettingLogs] = useGetDealerRolesDeviceSettingLogsPaginatedMutation();
  const fetchSettingLogs = isDealerRole ? fetchDealerSettingLogs : fetchStorageSettingLogs;

  const [fetchStorageOperationLogs] = useGetDeviceOperationLogsPaginatedMutation();
  const [fetchDealerOperationLogs] = useGetDealerRolesDeviceOperationLogsPaginatedMutation();
  const fetchOperationLogs = isDealerRole ? fetchDealerOperationLogs : fetchStorageOperationLogs;

  // Bilgi değişiklik logları bayide YOK (#302) — menü öğesi devices.reportInfo
  // iznine bağlı ve bu anahtar bayi için düşer; yalnız depo ucu kalır.
  const [fetchInfoLogs] = useGetDeviceInfoLogsPaginatedMutation();

  // ── Görünüm state'leri ──
  const [loading, setLoading] = useState(false);

  const [operationData, setOperationData] = useState(null);

  const [perfPeriod, setPerfPeriod] = useState("week");
  const [perfWeek, setPerfWeek] = useState(null);
  const [perfMonth, setPerfMonth] = useState(null);
  const [perfCustom, setPerfCustom] = useState(null);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);

  const [listItems, setListItems] = useState([]);
  const [listPage, setListPage] = useState(0);
  const [listHasMore, setListHasMore] = useState(false);
  const [listLoadingMore, setListLoadingMore] = useState(false);

  const [selectedOperation, setSelectedOperation] = useState(null);
  const [operationTab, setOperationTab] = useState("summary");
  const [selectedInfoLog, setSelectedInfoLog] = useState(null);

  const listFetcher =
    initialView === "settingLogs"
      ? fetchSettingLogs
      : initialView === "operationLogs"
        ? fetchOperationLogs
        : initialView === "infoLogs"
          ? fetchInfoLogs
          : null;

  // ── Açılışta seçilen görünümün verisini çek ──
  const loadView = useCallback(async () => {
    if (!device?._id || !token || !initialView) return;
    setLoading(true);
    try {
      if (initialView === "lastOperation" && !isDealerRole) {
        const res = await getDeviceLastOperation({
          token, deviceId: device._id, deviceStatusName: device.deviceStatusName,
        }).unwrap();
        setOperationData(res.data?.lastInfoOperation || null);
      } else if (initialView === "openPerformance") {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const [week, month] = await Promise.all([
          getStats({ id: device._id, token, type: "week", tz }).unwrap(),
          getStats({ id: device._id, token, type: "thismonth", tz }).unwrap(),
        ]);
        setPerfWeek(normalizePerformanceTotals(week?.data));
        setPerfMonth(normalizePerformanceTotals(month?.data));
      } else if (listFetcher) {
        const res = await listFetcher({ token, deviceId: device._id, page: 0, limit: PAGE_LIMIT }).unwrap();
        setListItems(res.data || []);
        setListHasMore(res.hasMore);
        setListPage(0);
      }
    } catch (e) {
      // Veri alınamazsa boş durum gösterilir
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?._id, token, initialView]);

  useEffect(() => {
    if (!open) return;
    loadView();
  }, [open, loadView]);

  // Görünüm state'leri kapanış kayması BİTTİKTEN sonra sıfırlanır; `open`e
  // bağlıyken içerik drawer hâlâ ekrandayken boşalıyordu.
  useEffect(() => {
    if (mounted) return;
    setOperationData(null);
    setPerfWeek(null);
    setPerfMonth(null);
    setPerfCustom(null);
    setPerfPeriod("week");
    setShowCustomRange(false);
    setCustomStart(null);
    setCustomEnd(null);
    setListItems([]);
    setListPage(0);
    setListHasMore(false);
    setSelectedOperation(null);
    setSelectedInfoLog(null);
    setOperationTab("summary");
  }, [mounted]);

  const loadMore = async () => {
    if (!listFetcher || !device?._id) return;
    const nextPage = listPage + 1;
    setListLoadingMore(true);
    try {
      const res = await listFetcher({ token, deviceId: device._id, page: nextPage, limit: PAGE_LIMIT }).unwrap();
      setListItems((prev) => [...prev, ...(res.data || [])]);
      setListHasMore(res.hasMore);
      setListPage(nextPage);
    } catch (e) {
      // sessiz geç
    } finally {
      setListLoadingMore(false);
    }
  };

  const handleCustomRangeSearch = async () => {
    if (!customStart || !customEnd || !device?._id) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startStr = customStart instanceof Date ? customStart.toISOString().split("T")[0] : customStart;
    const endStr = customEnd instanceof Date ? customEnd.toISOString().split("T")[0] : customEnd;
    setCustomLoading(true);
    try {
      const res = await getStats({
        id: device._id, token, type: "custom", tz, customStart: startStr, customEnd: endStr,
      }).unwrap();
      setPerfCustom(normalizePerformanceTotals(res?.data));
      setPerfPeriod("custom");
    } catch (e) {
      // sessiz geç
    } finally {
      setCustomLoading(false);
    }
  };

  const renderStepDetails = (step = {}) => {
    const hiddenKeys = new Set(["type", "message", "createdInfo"]);
    const entries = Object.entries(step).filter(([key, value]) => !hiddenKeys.has(key) && value !== undefined);
    if (entries.length === 0) return null;
    return (
      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
        {entries.map(([key, value]) => (
          <div key={key} className="flex flex-col">
            <span className="text-xs text-gray-400">{getStepKeyLabel(key)}</span>
            <span className="text-sm text-gray-700 break-words">{formatStepValue(key, value)}</span>
          </div>
        ))}
      </div>
    );
  };

  // ── Görünümler ──

  const renderLastOperation = () => {
    if (!operationData) {
      return <EmptyView message="İşlem kaydı bulunamadı" />;
    }
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-400 font-semibold tracking-wider mb-1">İŞLEMİ YAPAN</p>
            <p className="text-base font-semibold text-gray-800 truncate">{operationData.operationManager || "-"}</p>
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-400 font-semibold tracking-wider mb-1">TARİH</p>
            <p className="text-base font-semibold text-gray-800">{formatDate(operationData.createdInfo)}</p>
          </div>
        </div>
        {operationData.createdInfo && (
          <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 rounded-xl">
            <FaClock className="w-4 h-4 text-indigo-500 shrink-0" />
            <span className="text-sm font-semibold text-indigo-700">{getTimeSince(operationData.createdInfo)}</span>
          </div>
        )}
        {(operationData.valueFrom || operationData.valueTo) && (
          <div className="flex items-center gap-3 px-2">
            <div className="flex-1 text-center"><LocationBadge value={operationData.valueFrom || "-"} /></div>
            <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
              <FaArrowRight className="w-3 h-3 text-indigo-500" />
            </div>
            <div className="flex-1 text-center"><LocationBadge value={operationData.valueTo || "-"} /></div>
          </div>
        )}
        {operationData.note && (
          <div className="bg-amber-50 rounded-xl px-4 py-3">
            <p className="text-xs text-amber-600 font-semibold tracking-wider mb-1">NOT</p>
            <p className="text-sm text-gray-700 leading-relaxed">{displayLogText(operationData.note)}</p>
          </div>
        )}
        {operationData.description && operationData.description !== "Açıklama yok" && (
          <div className="bg-teal-50 rounded-xl px-4 py-3">
            <p className="text-xs text-teal-600 font-semibold tracking-wider mb-1">AÇIKLAMA</p>
            <p className="text-sm text-gray-700 leading-relaxed">{displayLogText(operationData.description)}</p>
          </div>
        )}
      </div>
    );
  };

  const renderPerformance = () => {
    const summary =
      perfPeriod === "week" ? perfWeek : perfPeriod === "month" ? perfMonth : perfCustom;
    return (
      <div className="space-y-4">
        {/* Dönem seçimi */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1 flex-1 p-1 bg-gray-100 rounded-xl">
            <button
              type="button"
              onClick={() => setPerfPeriod("week")}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                perfPeriod === "week" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Haftalık
            </button>
            <button
              type="button"
              onClick={() => setPerfPeriod("month")}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                perfPeriod === "month" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Aylık
            </button>
            {perfCustom && (
              <button
                type="button"
                onClick={() => setPerfPeriod("custom")}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                  perfPeriod === "custom" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Özel
              </button>
            )}
          </div>
          {hasPermission("devices.reportConsumption") && (
            <button
              type="button"
              onClick={() => setShowCustomRange(!showCustomRange)}
              className={`p-3 rounded-xl transition-colors ${
                showCustomRange ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              title="Özel tarih aralığı"
            >
              <FaCalendarAlt className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Özel tarih aralığı */}
        {showCustomRange && hasPermission("devices.reportConsumption") && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
            <div className="flex items-center gap-2">
              <DateRangeField
                startDate={customStart}
                setStartDate={setCustomStart}
                endDate={customEnd}
                setEndDate={setCustomEnd}
                size="sm"
                startLabel=""
                endLabel=""
                startPlaceholder="Başlangıç"
                endPlaceholder="Bitiş"
                zIndex={10005}
                className="contents"
              />
              <button
                type="button"
                onClick={handleCustomRangeSearch}
                disabled={!customStart || !customEnd || customLoading}
                className="p-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors disabled:opacity-40 shrink-0"
              >
                <FaSearch className="w-4 h-4" />
              </button>
            </div>
          </LocalizationProvider>
        )}

        {customLoading ? (
          <LoadingView message="Rapor hazırlanıyor..." count={2} />
        ) : !summary || (summary.totalConsumption === 0 && summary.productTotals.length === 0) ? (
          <EmptyView message="Tüketim yok" suggestion="Bu dönemde kayıt bulunamadı." />
        ) : (
          <>
            {/* Toplam tüketim */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-indigo-700">Toplam Kota Tüketimi</span>
              <span className="text-2xl font-extrabold text-indigo-700 tabular-nums">
                {summary.totalConsumption.toLocaleString("tr-TR")}
              </span>
            </div>

            {/* Ürün dağılımı */}
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-400 font-semibold tracking-wider mb-3">ÜRÜN DAĞILIMI</p>
              <div className="space-y-3">
                {summary.productTotals.map((item) => {
                  const pct = summary.totalConsumption > 0 ? Math.round((item.total / summary.totalConsumption) * 100) : 0;
                  const barColor = colorOfProduct[item.productName] || chartColors.neutral;
                  return (
                    <div key={item.productName}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: barColor }} />
                          <span className="text-sm font-medium text-gray-800 truncate">{item.productName}</span>
                        </span>
                        <span className="text-sm font-bold text-gray-700 tabular-nums shrink-0">
                          {item.total.toLocaleString("tr-TR")}{" "}
                          <span className="text-xs font-normal text-gray-400">(%{pct})</span>
                        </span>
                      </div>
                      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: barColor }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Saatlik dağılım */}
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-400 font-semibold tracking-wider mb-3">SAATLİK DAĞILIM</p>
              <HourlyChart hourlyData={summary.hourlyData} />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderSettingLogs = () => {
    if (listItems.length === 0) {
      return <EmptyView message="Kayıt bulunamadı" />;
    }
    return (
      <div className="space-y-3">
        {listItems.map((log, idx) => (
          <div key={log._id || idx} className="bg-white border border-gray-100 rounded-xl px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">{log.name} {log.lastName}</span>
              <span className="text-sm text-gray-400">{formatDate(log.createdInfo)}</span>
            </div>
            {(log.info || []).map(displayLogEntry).map((change, ci) => (
              <div key={ci} className="flex items-start gap-2 py-1 flex-wrap">
                <span className="text-sm font-medium text-gray-700 shrink-0">{change.infoName}:</span>
                <span className="text-sm text-red-500 line-through">{change.valueFrom}</span>
                <FaArrowRight className="w-3.5 h-3.5 text-gray-300 mt-0.5 shrink-0" />
                <span className="text-sm text-green-600 font-medium">{change.valueTo}</span>
              </div>
            ))}
          </div>
        ))}
        {listHasMore && <LoadMoreButton onClick={loadMore} loading={listLoadingMore} />}
      </div>
    );
  };

  const renderOperationLogs = () => {
    // Detay görünümü — Özet / Adımlar sekmeleri
    if (selectedOperation) {
      const opMeta = selectedOperation.metadata;
      const metaEntries =
        opMeta && typeof opMeta === "object"
          ? Object.entries(opMeta).filter(([, v]) => v !== undefined && v !== null && v !== "")
          : [];
      const fmtMeta = (key, value) =>
        typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)
          ? formatDate(value)
          : formatStepValue(key, value);
      return (
        <div className="space-y-3">
          <button
            onClick={() => setSelectedOperation(null)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl px-4 py-2.5 hover:bg-gray-200 transition-colors"
          >
            <FaArrowLeft className="w-3.5 h-3.5" />
            Listeye Dön
          </button>

          <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
            <p className="text-base font-bold text-gray-800">{selectedOperation.operationName}</p>
            <p className="text-sm text-gray-400 mt-0.5">{formatDate(selectedOperation.createdInfo)}</p>
          </div>

          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
            {[
              { key: "summary", label: "Özet" },
              { key: "steps", label: "Adımlar" },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setOperationTab(t.key)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                  operationTab === t.key ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {operationTab === "summary" ? (
            metaEntries.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">Bu operasyon için özet bilgisi yok.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {metaEntries.map(([key, value]) => (
                  <div key={key} className="rounded-xl border bg-white border-gray-100 px-3 py-2.5 min-w-0">
                    <div className="text-xs leading-tight text-gray-400 truncate">
                      {getMetadataLabel(key, opMeta.productName, selectedOperation?.operationName)}
                    </div>
                    <div className="text-sm font-semibold text-gray-800 break-words mt-0.5">
                      {fmtMeta(key, value)}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-2">
              {(selectedOperation.operationSteps || []).length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">Adım bulunamadı.</p>
              ) : (
                (selectedOperation.operationSteps || []).map((step, si) => {
                  const style = getStepStyle(step?.type);
                  return (
                    <div key={si} className={`rounded-xl border px-3.5 py-2.5 ${style.bg}`}>
                      <div className="flex items-center gap-2">
                        {style.icon}
                        <span className={`text-sm font-semibold ${style.text}`}>{getStepLabel(step?.type)}</span>
                        {step?.createdInfo && (
                          <span className="ml-auto text-sm text-gray-400">{formatDate(step.createdInfo)}</span>
                        )}
                      </div>
                      {step?.message && <p className="text-sm text-gray-600 mt-1">{step.message}</p>}
                      {renderStepDetails(step)}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      );
    }

    if (listItems.length === 0) {
      return <EmptyView message="Kayıt bulunamadı" />;
    }
    return (
      <div className="space-y-2">
        {listItems.map((log, idx) => {
          const steps = log.operationSteps || [];
          const hasMeta = log.metadata && typeof log.metadata === "object" && Object.keys(log.metadata).length > 0;
          const hasDetail = steps.length > 0 || hasMeta;
          return (
            <button
              key={log._id || idx}
              onClick={() => {
                if (!hasDetail) return;
                setOperationTab(hasMeta ? "summary" : "steps");
                setSelectedOperation(log);
              }}
              className={`w-full text-left rounded-xl border bg-white border-gray-100 px-4 py-3 transition-colors ${
                hasDetail ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-800 truncate">{log.operationName || "-"}</span>
                <span className="text-sm text-gray-400 shrink-0">{formatDate(log.createdInfo)}</span>
              </div>
              {log.description && <p className="text-sm text-gray-500 mt-1 truncate">{displayLogText(log.description)}</p>}
            </button>
          );
        })}
        {listHasMore && <LoadMoreButton onClick={loadMore} loading={listLoadingMore} />}
      </div>
    );
  };

  const renderInfoLogs = () => {
    if (selectedInfoLog) {
      return (
        <div className="space-y-3">
          <button
            onClick={() => setSelectedInfoLog(null)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl px-4 py-2.5 hover:bg-gray-200 transition-colors"
          >
            <FaArrowLeft className="w-3.5 h-3.5" />
            Listeye Dön
          </button>

          <div className="bg-white border border-gray-100 rounded-xl px-4 py-3">
            <p className="text-base font-bold text-gray-800">{selectedInfoLog.name} {selectedInfoLog.lastName}</p>
            <p className="text-sm text-gray-400 mt-0.5">{formatDate(selectedInfoLog.createdInfo)}</p>
          </div>

          <div className="space-y-2">
            {(selectedInfoLog.info || []).map((rawChange, ci) => {
              const change = displayLogEntry(rawChange);
              let label = change.infoName;
              if (change.infoName === "Ürün bilgileri" && change.description) {
                const match = change.description.match(/\.\s+(.+?)\s+ürününün/);
                if (match) label = match[1];
              }
              return (
                <div key={ci} className="rounded-xl border border-gray-100 bg-white px-4 py-3">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-700 shrink-0">{label}:</span>
                    <span className="text-sm text-red-500 line-through">{change.valueFrom}</span>
                    <FaArrowRight className="w-3.5 h-3.5 text-gray-300 mt-0.5 shrink-0" />
                    <span className="text-sm text-green-600 font-medium">{change.valueTo}</span>
                  </div>
                  {change.description && <p className="text-sm text-gray-500 mt-1.5">{change.description}</p>}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (listItems.length === 0) {
      return <EmptyView message="Kayıt bulunamadı" />;
    }
    return (
      <div className="space-y-2">
        {listItems.map((log, idx) => (
          <button
            key={log._id || idx}
            onClick={() => setSelectedInfoLog(log)}
            className="w-full text-left rounded-xl border bg-white border-gray-100 px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-700 truncate">{log.name} {log.lastName}</span>
              <span className="text-sm text-gray-400 shrink-0">{formatDate(log.createdInfo)}</span>
            </div>
            {(log.info || []).length > 0 && (
              <p className="text-sm text-gray-500 truncate">
                {(log.info || []).map((c) => displayLogInfoName(c.infoName)).filter(Boolean).join(", ")}
              </p>
            )}
          </button>
        ))}
        {listHasMore && <LoadMoreButton onClick={loadMore} loading={listLoadingMore} />}
      </div>
    );
  };

  const renderBody = () => {
    if (loading) return <LoadingView message={`${meta?.title || "Detay"} yükleniyor...`} />;
    switch (initialView) {
      case "lastOperation":
        return renderLastOperation();
      case "openPerformance":
        return renderPerformance();
      case "settingLogs":
        return renderSettingLogs();
      case "operationLogs":
        return renderOperationLogs();
      case "infoLogs":
        return renderInfoLogs();
      default:
        return null;
    }
  };

  return (
    <>
      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[560px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9997] flex flex-col bg-gray-50`}
      >
        {/* Header — görünümün kendi rengiyle (diğer drawer'lardaki desen) */}
        <div
          className={`flex items-center justify-between px-5 py-4 shrink-0 text-white bg-gradient-to-r ${
            meta?.gradient || DEFAULT_HEADER_GRADIENT
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            {meta && (
              <div className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0 bg-white/20 backdrop-blur-sm">
                <meta.icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-bold tracking-wide truncate">{meta?.title || "Cihaz Detayı"}</h3>
              <p className="text-sm text-white/80 truncate">
                {device?.customName || device?.deviceName}
                <span className="ml-1.5 text-white/60 font-mono">{device?.serialNo}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors shrink-0"
            aria-label="Kapat"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">{renderBody()}</div>
      </div>

      {/* Backdrop */}
      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9996] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          onClick={onClose}
        />
      )}
    </>
  );
};

export default DeviceOperationsDrawer;
